import { useNavigation, useRoute } from '@react-navigation/native';
import QRCameraReader from '_/components/QRCameraReader';
import {
  QRCODE_LOCATION_PREFIX,
  QRCODE_SLOT_PREFIX,
  QRCODE_SPACE_PREFIX,
} from '_/config/qrCodePrefix';
import { colors } from '_/constants/theme';
import { networkErrorMessage } from '_/helpers/networkError';
import { useEventContext } from '_/hooks/EventContext';
import { useLanguage } from '_/hooks/LanguageContext';
import useFirebaseAnalytics from '_/hooks/useFirebaseAnalytics';
import { eventsApi } from '_/services/api';
import { EventsResponse } from '_/services/models/events.model';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showMessage } from 'react-native-flash-message';
import { checkinErrorMessage } from '_/utils/checkinErros';

interface ParamsProps {
  event: EventsResponse;
}

export default function QRCodeScanner() {
  const { getTodayEvents } = useEventContext();
  const [loading, setLoading] = useState(false);
  const navigation = useNavigation<any>();
  const route = useRoute();
  const { event } = route.params as ParamsProps;

  const { analyticsLogEvent } = useFirebaseAnalytics();
  const { t } = useTranslation();
  const { locale } = useLanguage();

  async function handleQRCodeScanned(data: string) {
    setLoading(true);
    try {
      const prefix = data.slice(0, data.lastIndexOf('/') + 1);
      switch (prefix) {
        case QRCODE_SLOT_PREFIX: {
          const slotId = data.replace(QRCODE_SLOT_PREFIX, '');

          if (slotId !== event.slotId) {
            showMessage({
              message: t('error'),
              description: t('qrCodeScreen.errorMessages.wrongSlotQrCode'),
              backgroundColor: colors.errorRed,
            });
            return;
          }

          await eventsApi.checkinByQrCode({ id: event.id, slotId });
          break;
        }
        case QRCODE_LOCATION_PREFIX: {
          const locationId = data.replace(QRCODE_LOCATION_PREFIX, '');

          if (locationId !== event.slot?.space?.location?.id) {
            showMessage({
              message: t('error'),
              description: t('qrCodeScreen.errorMessages.wrongSlotQrCode'),
              backgroundColor: colors.errorRed,
            });
            throw new Error();
          }

          await eventsApi.checkinByQrCode({ id: event.id, slotId: event.slotId });
          break;
        }
        case QRCODE_SPACE_PREFIX: {
          const spaceId = data.replace(QRCODE_SPACE_PREFIX, '');

          if (spaceId !== event.slot?.space?.id) {
            showMessage({
              message: t('error'),
              description: t('qrCodeScreen.errorMessages.wrongSlotQrCode'),
              backgroundColor: colors.errorRed,
            });
            throw new Error();
          }

          await eventsApi.checkinByQrCode({ id: event.id, slotId: event.slotId });
          break;
        }
        default:
          showMessage({
            message: t('error'),
            description: t('qrCodeScreen.errorMessages.invalidQrCode'),
            backgroundColor: colors.errorRed,
          });
          return;
      }

      showMessage({
        message: t('success'),
        description: t('qrCodeScreen.successMessages.checkinSuccess'),
        backgroundColor: colors.successGreen,
      });
      analyticsLogEvent({
        name: 'makeCheckin',
        properties: {
          name: 'makeCheckin',
          screen: 'QRCodeScanner',
          purpose: 'Checkin event',
        },
      });

      navigation.goBack();
      await getTodayEvents();
    } catch (error: any) {
      analyticsLogEvent({
        name: 'errorMakeCheckin',
        properties: {
          name: 'errorMakeCheckin',
          screen: 'QRCodeScanner',
          purpose: 'Checkin event',
        },
      });
      networkErrorMessage(error);
      checkinErrorMessage(error, t, locale);
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  }

  return (
    <QRCameraReader onScanned={handleQRCodeScanned} onClose={navigation.goBack} loading={loading} />
  );
}
